/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ShouldRevalidateFunctionArgs } from 'react-router';
import { getActionPath, isAmbientMutation } from './shared';

/**
 * `shouldRevalidate` for the category layout that owns horizontal navigation.
 *
 * Nav depends on `categoryId` (+ ambient site/locale/currency/auth), not on PLP
 * `sort` / `refine` / `offset` / filters-panel params. Returning `false` for
 * same-pathname search changes keeps the layout loader from re-running its
 * SCAPI eligibility fan-out while the index route still revalidates the grid.
 *
 * @see docs/README-REVALIDATION.md — "Navigation: skip re-fetch when only query params change"
 */
export function shouldRevalidate({
    currentUrl,
    nextUrl,
    currentParams,
    nextParams,
    formMethod,
    formAction,
    defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs): boolean {
    if (currentParams.categoryId !== nextParams.categoryId) {
        return true;
    }

    // Same category: listing query/client-only param toggles must not re-resolve nav.
    if (currentUrl.pathname === nextUrl.pathname && currentUrl.search !== nextUrl.search) {
        return false;
    }

    if (formMethod && formMethod !== 'GET') {
        const actionPath = getActionPath(formAction, currentUrl.origin);
        return Boolean(actionPath && isAmbientMutation(actionPath));
    }

    return defaultShouldRevalidate;
}
